import React, { useEffect, useMemo } from "react";
import styles from "./SideBar.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import classNames from "classnames";
import { resetConfluenceStore } from "../../redux/confluenceStore";
import Separator from "../../components/Separator/Separator";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import DefineIcon from "../../icons/define-new.png";
import MeasureIcon from "../../icons/measure-new.png";
import AnalyseIcon from "../../icons/analyze-new.png";
import ImproveIcon from "../../icons/improve-new.png";
import ControlIcon from "../../icons/control-new.png";
import LogoWhite from "../../icons/borderly-plus-white-logo.svg";
import BorderlyIcon from "../../icons/borderly-icon.png";
import {
  LayoutDashboard,
  FileSearch,
  AlertCircle,
  TrendingUp,
  Target,
  BookOpen,
  Package,
  Shield,
  BarChart3,
  Settings,
  Users,
  ClipboardCheck,
  LineChart,
  FileText,
  FileBarChart,
  Search,
  Network,
  Database,
  Sparkles,
  UserPlus,
  Archive,
  PlusCircle,
  PieChart,
} from "lucide-react";
import { resolveInsightsOrigin } from "../../utils/auth";

const SIDEBAR_EXPANDED = 255;
const SIDEBAR_COLLAPSED = 48;

// Session modules can still include the legacy insights batch-upload screens.
const isBatchUploadModule = (module) => {
  const title = (module?.title || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  const path = (module?.path || "").toLowerCase();

  if (
    title === "upload batches" ||
    title === "select batches" ||
    title === "batch upload" ||
    title === "upload batch"
  ) {
    return true;
  }

  return (
    path.includes("upload-batch") ||
    path.includes("batch-upload") ||
    path.includes("select-batches") ||
    /(?:^|\/)batches\/?$/.test(path)
  );
};

const SideBar = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const user = useSelector((state) => state.user.user);
  const menuActive = useSelector((state) => state.layout.menuActive);

  //Getting data through orgId; If in some case orgId is not always in URL, set orgId in local storage or use other method;
  const modules = useSelector((state) => state.user.modules[orgId]);

  const isMobile = useMediaQuery("(max-width:768px)");

  const normalize = (value) => value?.toLowerCase() || "";
  const resolvedOrgId = orgId || localStorage.getItem("org_id") || "";

  const setSharedCookie = (name, val) => {
    const isLocal = window.location.hostname === "localhost";
    const cookieDomain = isLocal ? "" : "domain=.border.ly;";
    document.cookie = `${name}=${val}; ${cookieDomain} path=/; secure; samesite=None`;
  };

  useEffect(() => {
    const width = menuActive ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;
    document.documentElement.style.setProperty(
      "--sidebar-offset",
      `${width}px`,
    );
  }, [menuActive]);

  const moduleIconMap = useMemo(
    () => ({
      "quality system (qms)": Target,
      "quality management system": Target,
      "quality measurement system": Target,
      permits: FileText,
      audits: FileSearch,
      findings: AlertCircle,
      "risk analysis": BarChart3,
      "risk profile": Shield,
      "risk control center": Shield,
      "improvement projects": TrendingUp,
      dashboard: LayoutDashboard,
      home: LayoutDashboard,
      control: ClipboardCheck,
      archived: Archive,
      "new finding": PlusCircle,
      resources: BookOpen,
      "knowledge base": BookOpen,
      "management reports": FileBarChart,
      admin: Settings,
      "role management": Users,
      "client onboarding": UserPlus,
      "customs insights": PieChart,
      "taric explorer": Search,
      analytics: LineChart,
      reports: FileText,
      upload: FileBarChart,
      hierarchy: Network,
      transactions: Database,
      "product passport": Package,
      enrichment: Sparkles,
      rules: Settings,
      insights: LineChart,
      analysis: BarChart3,
    }),
    [],
  );

  const extendedModules = useMemo(() => {
    const baseModules = (Array.isArray(modules) ? modules : []).filter(
      (module) => !isBatchUploadModule(module),
    );
    const additions = [];

    if (
      !baseModules.some(
        (module) =>
          normalize(module?.title) === "risk control center" ||
          module?.path === "/risk-control-center",
      )
    ) {
      additions.push({
        title: "Risk Control Center",
        path: "/risk-control-center",
        category: "Analyse",
      });
    }

    if (user?.Role === "Consultant") {
      additions.push({
        title: t("sidebar.archived"),
        path: "/archived",
        category: "", // put it in General section
      });
    }

    return [...baseModules, ...additions];
  }, [modules, normalize, t, user?.Role]);

  const isDashboardModule = (module) =>
    module?.path === "/" || normalize(module?.title) === "home";

  const dashboardModule = extendedModules?.find(isDashboardModule);

  const newFindingModule = extendedModules?.find(
    (module) => normalize(module.title) === "new finding",
  );

  const groupedModules = extendedModules?.reduce((acc, module) => {
    if (normalize(module.title) === "new finding" && !isMobile) {
      return acc;
    }
    if (!isMobile && isDashboardModule(module)) {
      return acc;
    }
    const category = module.category || "";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(module);
    return acc;
  }, {});

  const categoryOrder = ["Define", "Measure", "Analyse", "Improve", "Control"];

  const categoryPriority = (category) => {
    const idx = categoryOrder.findIndex(
      (item) => normalize(item) === normalize(category),
    );
    return idx === -1 ? categoryOrder.length : idx;
  };

  const sortedCategories = Object.keys(groupedModules || {}).sort(
    (a, b) => categoryPriority(a) - categoryPriority(b),
  );

  const validRoutes = [
    "/organizations",
    "/",
    "/qms",
    "/resources",
    "/knowledge-base",
    "/management-reports",
    "/audits",
    "/improvement-projects",
    "/findings",
    "/risk-analysis",
    "/risk-control-center",
    "/manage-audits",
    "/admin",
    "/new-finding",
    "/copy-audits",
    "/risk-profile",
    "/archived",
  ];

  const handleNavigation = (path) => {
    if (isCurrentPath(path)) {
      return;
    }

    dispatch(resetConfluenceStore());
    navigate(`/${orgId}${path}`);
  };

  const handleOpenInsights = () => {
    if (resolvedOrgId) setSharedCookie("org_id", resolvedOrgId);

    const base = resolveInsightsOrigin();
    // ci.border.ly is the product root. The legacy insights app routed by org id.
    if (base === "https://ci.border.ly") {
      window.open(base, "_blank", "noopener,noreferrer");
      return;
    }

    const target = resolvedOrgId
      ? `${base}/${encodeURIComponent(resolvedOrgId)}`
      : `${base}/organizations`;
    window.location.assign(target);
  };

  const isPathValid = (path) => {
    const resolvedRoutes = validRoutes.map((route) =>
      route.replace(":orgId", orgId),
    );
    return resolvedRoutes.includes(path);
  };

  const isCurrentPath = (path) => {
    return location.pathname === `/${orgId}` && path === "/"
      ? true
      : location.pathname === `/${orgId}${path}`;
  };

  const translateModuleTitle = (title) => {
    if (!title) return "";

    if (normalize(title) === "home") {
      return t("sidebar.home", "Dashboard");
    }

    const key = title.replace(/\s+/g, "").toLowerCase();
    return t(`modules.${key}`, title);
  };

  const getCategoryMeta = (category) => {
    const normalized = normalize(category);
    const fallbackLabel =
      category || t("sidebar.categories.general", "General");

    switch (normalized) {
      case "define":
        return {
          label: t("sidebar.categories.define", "Define"),
          icon: DefineIcon,
          colorClass: styles.define,
        };
      case "measure":
        return {
          label: t("sidebar.categories.measure", "Measure"),
          icon: MeasureIcon,
          colorClass: styles.measure,
        };
      case "analyse":
        return {
          label: t("sidebar.categories.analyse", "Analyse"),
          icon: AnalyseIcon,
          colorClass: styles.analyse,
        };
      case "improve":
        return {
          label: t("sidebar.categories.improve", "Improve"),
          icon: ImproveIcon,
          colorClass: styles.improve,
        };
      case "control":
        return {
          label: t("sidebar.categories.control", "Control"),
          icon: ControlIcon,
          colorClass: styles.control,
          isGeneralRoot: false,
        };
      default:
        return {
          label: fallbackLabel,
          icon: null,
          colorClass: styles.general,
          isGeneralRoot: !category,
        };
    }
  };

  const renderMenuItem = (module, categoryMeta) => {
    const isValid = isPathValid(module.path);
    const isActive = isCurrentPath(module.path);
    const Icon = moduleIconMap[normalize(module.title)] || LayoutDashboard;

    return (
      <button
        key={module.path || module.title}
        type="button"
        className={classNames(
          styles.menuItem,
          categoryMeta.colorClass,
          categoryMeta.isGeneralRoot && styles.noSidePadding,
          isValid ? styles.enabled : styles.disabled,
          isActive && styles.active,
        )}
        onClick={() => isValid && handleNavigation(module.path)}
        disabled={!isValid}
      >
        <span className={styles.menuItemIcon}>
          <Icon size={16} aria-hidden />
        </span>
        <span className={styles.menuItemLabel}>
          {translateModuleTitle(module?.title)}
        </span>
      </button>
    );
  };

  const renderDashboardItem = (module) => {
    const isValid = isPathValid(module.path);
    const isActive = isCurrentPath(module.path);

    return (
      <button
        key={module.path || module.title}
        type="button"
        className={classNames(
          styles.menuItem,
          styles.general,
          isValid ? styles.enabled : styles.disabled,
          isActive && styles.active,
        )}
        onClick={() => isValid && handleNavigation(module.path)}
        disabled={!isValid}
      >
        <span className={styles.menuItemIcon}>
          <LayoutDashboard size={16} aria-hidden />
        </span>
        <span className={styles.menuItemLabel}>
          {t("sidebar.home", "Dashboard")}
        </span>
      </button>
    );
  };

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.expanded]: menuActive,
        [styles.collapsed]: !menuActive,
      })}
    >
      <div className={styles.logoSlot}>
        <img
          src={LogoWhite}
          alt="Borderly"
          className={classNames(
            styles.logo,
            styles.logoPrimary,
            menuActive ? styles.visible : styles.hidden,
          )}
        />
        <img
          src={BorderlyIcon}
          alt="Borderly"
          className={classNames(
            styles.logo,
            styles.logoSecondary,
            menuActive ? styles.hidden : styles.visible,
          )}
        />
      </div>
      {isMobile ? (
        <div className={styles.mobileMenu}>
          <button
            type="button"
            key="home"
            className={classNames(
              styles.menuItem,
              styles.general,
              isPathValid("/") ? styles.enabled : styles.disabled,
              isCurrentPath("/") && styles.active,
            )}
            disabled={!isPathValid("/")}
            onClick={() => isPathValid("/") && handleNavigation("/")}
          >
            <span className={styles.menuItemIcon}>
              <LayoutDashboard size={16} aria-hidden />
            </span>
            <span className={styles.menuItemLabel}>{t("sidebar.home")}</span>
          </button>
          {newFindingModule && (
            <button
              type="button"
              key={newFindingModule.path}
              className={classNames(
                styles.menuItem,
                styles.general,
                isPathValid(newFindingModule.path)
                  ? styles.enabled
                  : styles.disabled,
                isCurrentPath(newFindingModule.path) && styles.active,
              )}
              disabled={!isPathValid(newFindingModule.path)}
              onClick={() =>
                isPathValid(newFindingModule.path) &&
                handleNavigation(newFindingModule.path)
              }
            >
              <span className={styles.menuItemIcon}>
                <PlusCircle size={16} aria-hidden />
              </span>
              <span className={styles.menuItemLabel}>
                {t("sidebar.newFinding")}
              </span>
            </button>
          )}
        </div>
      ) : (
        <>
          <div className={styles.scrollArea}>
            {dashboardModule && renderDashboardItem(dashboardModule)}
            <div className={styles.sections}>
              {sortedCategories.map((category) => {
                const categoryMeta = getCategoryMeta(category);
                return (
                  <div key={category} className={styles.section}>
                    {menuActive && category && (
                      <div
                        className={classNames(
                          styles.badge,
                          categoryMeta.colorClass,
                        )}
                      >
                        {categoryMeta.icon && (
                          <img
                            src={categoryMeta.icon}
                            alt={`${category} icon`}
                            className={styles.badgeIcon}
                          />
                        )}
                        <span className={styles.badgeLabel}>
                          {categoryMeta.label}
                        </span>
                      </div>
                    )}

                    <div className={styles.sectionItems}>
                      {groupedModules[category].map((module) =>
                        renderMenuItem(module, categoryMeta),
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {user?.Role === "Consultant" && (
              <div className={styles.customsSection}>
                <div className={styles.customsLabel}>Customs Insights</div>
                <button
                  type="button"
                  className={styles.customsButton}
                  onClick={handleOpenInsights}
                >
                  <span className={styles.menuItemIcon}>
                    <LineChart size={16} aria-hidden />
                  </span>
                  <span className={styles.menuItemLabel}>Insights</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SideBar;
